import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from "formik"
import * as Yup from "yup"
import { useNavigate } from 'react-router-dom'

// components
import AppCard from '../../components/AppCard'
import AppForm from '../../components/form/form-time-off-balance/AppForm'
import ActionButtonForm from 'src/components/form/general-input-form/ActionButtonForm'
import AppModalConfirmation from '../../components/modal/AppModalConfirmation'

// helpers
import Axios from '../../helpers/axios'
import useToastMessage from 'src/helpers/toast-message'

const AddTimeOffBalance = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { showSuccess, showError } = useToastMessage();

  const isLoading = useSelector((state) => state.isLoading)
  const [showModalConfirm, setShowModalConfirm] = useState({ show: false })
  const [employeeOptions, setEmployeeOptions] = useState([])

  const formik = useFormik({
    initialValues: {
      employee_id: '',
      initial_balance: '',
    },
    validationSchema: Yup.object().shape({
      employee_id: Yup.string()
        .required("Please choose employee."),
      initial_balance: Yup.number()
        .required("Please enter initial balance.")
        .positive("Initial balance must be a positive number"),
    }),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting }) => {
      handleSave(values)
      setSubmitting(false)
    },
  });

  useEffect(() => {
    fetchEmployeeOptions()
  }, [])

  const fetchEmployeeOptions = () => {
    Axios({
      method: "get",
      url: `/api/tracker/v1/employee/option/list`,
    })
      .then(({ data }) => {
        if (data?.success) {
          const employeeOptions = data?.value.map((item) => ({
            value: item?.id,
            label: item?.name,
          }));
          setEmployeeOptions(employeeOptions);
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  }

  const handleInputValue = (values) => {
    const payload = {
      employee_id: values?.employee_id,
      leave_type_id: 1,
      year: new Date().getFullYear(),
      accrued_balance: values?.initial_balance,
    }

    return payload
  }

  const handlePost = (payload) => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    Axios({
      method: "post",
      url: `/api/tracker/v1/leave/balance/create`,
      data: payload
    })
      .then(({ data }) => {
        if (data?.success) {
          showSuccess('New time-off balance for an employee has been successfully created')
          navigate('/time-off-balance/list')
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const handleSelectEmployee = (data) => {
    formik.setValues((values) => ({
      ...values,
      employee_id: data ? data?.value : ''
    }), false)
  }

  const handleSave = (values) => {
    const inputPayload = handleInputValue(values)
    handlePost(inputPayload)
  }

  const handleSubmit = () => {
    formik.handleSubmit()

    setShowModalConfirm({ show: false })
  }

  return (
    <>
      {
        showModalConfirm.show && (
          <AppModalConfirmation
            confirmationType="confirmation"
            showModal={showModalConfirm}
            setShowModal={setShowModalConfirm}
            headerTitle="Confirmation"
            descriptionText="Are you sure want create a new time-off balance for an employee?"
            handleSubmit={handleSubmit}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
          />
        )
      }
      <AppCard
        className='mb-4'
        headerTitle={<h4>Add Time-Off Balance</h4>}
        bodyContent={
          <AppForm
            form="create"
            values={formik.values}
            handleChange={formik.handleChange}
            employeeOptions={employeeOptions}
            handleSelectEmployee={handleSelectEmployee}
            errors={formik.errors}
          />
        }
        footerContent={
          <ActionButtonForm
            isLoading={isLoading}
            disabled={formik.isSubmitting}
            onCancel={() => navigate('/time-off-balance/list')}
            onSave={() => setShowModalConfirm({ show: true })}
          />
        }
      />
    </>
  )
}

export default AddTimeOffBalance
